import { Head } from '@inertiajs/react'
import { useMemo } from 'react'
import AppLayout from '@/components/AppLayout.tsx'
import { plan_top_down_versioned_path } from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'
import PeriodPicker from './components/PeriodPicker'
import { formatVND } from './components/formatters'
import HeaderStat, { formatStatDate } from './components/HeaderStat'
import { type PlanNodeFromServer } from './components/VersionViewOverallSection'
import PlanBreakdownSections, { type ChannelSectionData, type QuarterlySectionData } from './components/PlanBreakdownSections'
import VersionMenu, { type VersionListItem } from './components/VersionMenu'
import { revisionLabels } from './components/periodLabel'

interface Props {
  slug: string
  begin_date: string
  end_date: string
  top_down_plan: {
    id: string
    name: string
    slug: string
    revenue_target: number
    currency: string | null
    org_node: { id: string; name: string; slug: string } | null
  }
  period: { start: string; end: string }
  // The plan period a revision's numbers cover (target months only); the
  // months before it are actual sales. Present for every version, but only
  // meaningful (narrower than begin_date–end_date) for a revision.
  plan_period: { start: string; end: string }
  // Number of months at the start of the plan already covered by actual
  // sales. Null for a plain version.
  actuals_months: number | null
  version: {
    id: string
    version_number: number
    name: string | null
    note: string | null
    published_at: string | null
    is_current: boolean
    actuals_months: number | null
  }
  versions: VersionListItem[]
  plan_nodes: PlanNodeFromServer[]
  channel_section: ChannelSectionData
  quarterly_section: QuarterlySectionData
}

function Header({
  name,
  slug,
  version,
  versions,
  orgNodeName,
  revenueTarget,
  currency,
  begin_date,
  end_date,
  planPeriod,
  actualsMonths,
  fullYearTarget,
}: {
  name: string
  slug: string
  version: Props['version']
  versions: VersionListItem[]
  orgNodeName: string | null
  revenueTarget: number
  currency: string | null
  begin_date: string
  end_date: string
  // A revision plans only the remaining months: the target below covers
  // planPeriod only, and fullYearTarget adds the actual months back in.
  planPeriod?: { start: string; end: string }
  actualsMonths?: number | null
  fullYearTarget?: number | null
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, width: '100%', paddingRight: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
        <h1 style={{ fontSize: 16, fontWeight: 700, color: 'var(--foreground)', margin: 0, letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
          {name}
        </h1>
        {orgNodeName && <HeaderStat label="Scope" value={orgNodeName} />}
        <HeaderStat
          label={actualsMonths ? 'Remaining Target' : 'Target'}
          value={formatVND(revenueTarget, currency)}
        />
        {actualsMonths && fullYearTarget != null && (
          <HeaderStat label="Full Target" value={formatVND(fullYearTarget, currency)} />
        )}
        <HeaderStat
          label={actualsMonths ? 'Remaining Period' : 'Period'}
          value={actualsMonths && planPeriod
            ? `${formatStatDate(planPeriod.start)} – ${formatStatDate(planPeriod.end)}`
            : `${formatStatDate(begin_date)} – ${formatStatDate(end_date)}`}
        />
      </div>
      <VersionMenu
        slug={slug}
        versions={versions}
        current={{
          version_number: version.version_number,
          published_at: version.published_at,
          name: version.name,
          is_draft: false,
          actuals_months: version.actuals_months,
        }}
        readOnly
      />
    </div>
  )
}

// Read-only view of one published version, served at /versions/:n.
export default function VersionView({
  slug,
  begin_date,
  end_date,
  top_down_plan,
  period,
  plan_period,
  actuals_months,
  version,
  versions,
  plan_nodes,
  channel_section,
  quarterly_section,
}: Props) {
  const market = useMarket()
  const versionLabel = version.name ? `v${version.version_number} — ${version.name}` : `v${version.version_number}`
  // Month labels for a revision's actual-vs-plan split; null for a plain
  // version, so every column that shows one carries its months.
  const periodLabels = useMemo(
    () => revisionLabels(plan_period, actuals_months, begin_date, period),
    [plan_period, actuals_months, begin_date, period]
  )
  // Full year = actual sales (top-level nodes only, to avoid double-counting
  // descendants) + this version's plan target.
  const fullYearTarget = useMemo(
    () => plan_nodes.reduce((sum, n) => sum + Number(n.actualRevenue ?? 0), 0) + top_down_plan.revenue_target,
    [plan_nodes, top_down_plan.revenue_target]
  )
  return (
    <AppLayout
      header={<Header
        name={top_down_plan.name}
        slug={slug}
        version={version}
        versions={versions}
        orgNodeName={top_down_plan.org_node?.name ?? null}
        revenueTarget={top_down_plan.revenue_target}
        currency={top_down_plan.currency}
        begin_date={begin_date}
        end_date={end_date}
        planPeriod={plan_period}
        actualsMonths={actuals_months}
        fullYearTarget={periodLabels ? fullYearTarget : null}
      />}
    >
      <Head title={`${top_down_plan.name} — ${versionLabel}`} />
      <div className="mb-4 flex justify-end">
        <div
          style={{ background: 'var(--background)', borderColor: 'var(--border)' }}
          className="border rounded-lg px-4 py-2.5"
        >
          <PeriodPicker
            planId={top_down_plan.id}
            beginDate={begin_date}
            endDate={end_date}
            start={period.start}
            end={period.end}
            path={plan_top_down_versioned_path(market.slug, slug, version.version_number)}
            only={['period', 'plan_nodes', 'channel_section', 'quarterly_section']}
          />
        </div>
      </div>
      <PlanBreakdownSections
        planNodes={plan_nodes}
        channelSection={channel_section}
        quarterlySection={quarterly_section}
        currency={top_down_plan.currency}
        periodLabels={periodLabels}
      />
    </AppLayout>
  )
}
